import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";

import DefaultAvatar from "../DefaultAvatar";
import { useGoingOut } from "../../Services/GoingOutContext";
import { friendStatusLabel } from "./goingOutLabels";
import type { GoingOutFriend } from "../../data/types";
import "../../Styles/FriendsGoingOut.css";

/**
 * "Friends going out tonight" i flödet.
 *
 * EN SEKTION, INTE INLÄGG. Varje svar är inte en händelse värd en egen rad
 * i flödet — den som följer tio personer skulle få tio kort om ingenting,
 * och ett elfte när någon ändrar sig. Hela kvällen ryms i en kompakt lista
 * högst upp, och den försvinner av sig själv klockan fem.
 *
 * OUT och MAYBE står var för sig på varje rad. Ingen sammanslagning, inget
 * "ute i kväll" över någon som svarat "kanske" — det var precis den
 * genvägen som gjorde sammanfattningen osann.
 *
 * NOT_TONIGHT finns inte här, och kan inte komma hit: servern filtrerar
 * bort läget i databasfrågan. Se visibleGoingOutWhere i api/goingOut.js.
 */
export default function FriendsGoingOut() {
  const { state } = useGoingOut();

  const friends = state?.friends ?? [];
  if (!friends.length) return null;

  // Ute först, kanske sedan. Den som bestämt sig är den mest användbara
  // uppgiften för den som funderar på att gå ut.
  const sorted = [...friends].sort((a, b) =>
    a.status === b.status ? 0 : a.status === "OUT" ? -1 : 1
  );

  return (
    <section className="friends-going-out" aria-labelledby="friends-going-out-heading">
      <h2 id="friends-going-out-heading">Friends going out tonight</h2>

      <ul className="friends-going-out-list">
        {sorted.map((friend) => (
          <FriendRow key={friend.user.id} friend={friend} />
        ))}
      </ul>
    </section>
  );
}

function FriendRow({ friend }: { friend: GoingOutFriend }) {
  const { user, status, activeCheckIn } = friend;

  return (
    <li className={`friends-going-out-item friends-going-out-item--${status.toLowerCase()}`}>
      <Link to={`/profile/${user.id}`} className="friends-going-out-person">
        <DefaultAvatar name={user.displayName} avatarUrl={user.avatarUrl ?? undefined} />

        <div className="friends-going-out-text">
          <span className="friends-going-out-name">{user.displayName}</span>

          {/* Är hen redan någonstans är det den uppgiften som gäller.
              "På Trädgården" är mer sant och mer användbart än "ska ut" —
              men bara när betraktaren får se check-in:en, vilket servern
              redan avgjort innan fältet ens finns här. */}
          {activeCheckIn ? (
            <span className="friends-going-out-venue">
              <MapPin size={12} strokeWidth={2.2} />
              At {activeCheckIn.venue.name}
            </span>
          ) : (
            <span className="friends-going-out-status">{friendStatusLabel(status)}</span>
          )}
        </div>
      </Link>

      {/* Läget står kvar även när platsen tagit över raden. Att någon är
          på ett ställe säger inte om hen svarat "ja" eller "kanske". */}
      <span className="friends-going-out-badge">{friendStatusLabel(status)}</span>
    </li>
  );
}
